import { AdvancedExchanges } from "@redotech/redo-return-core-common/exchange-settings";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type {
  PortalSettingsPatch,
  ReturnsPortalSettingsSource,
} from "./portal-settings-source";

const INSTANT_HOLD_KINDS = ["none", "one_dollar", "full_cost"] as const;

export const customerOptionsSettingsShape = {
  customerOptions: z.object({
    newProductSelection: createEnum(AdvancedExchanges),
    instant: z.boolean(),
    instantHoldAmount: z.enum(INSTANT_HOLD_KINDS),
    instantExchangeOnly: z.boolean(),
    instantExpirationDays: z.number().int().min(0),
    excludeProductTags: z.array(z.string()),
    excludeProductSkus: z.array(z.string()),
    excludeProductMetafields: z.array(z.string()),
    excludeVariantMetafields: z.array(z.string()),
    inventoryMinimum: z.number().int().min(0),
    hideOutOfStockVariants: z.boolean(),
    hideUnpublishedVariants: z.boolean(),
  }),
};

export type CustomerOptionsSettings = z.infer<
  z.ZodObject<typeof customerOptionsSettingsShape>
>;

export function customerOptionsSettingsFromTeam({
  settings,
}: ReturnsPortalSettingsSource): CustomerOptionsSettings {
  const { exchanges } = settings;
  return {
    customerOptions: {
      newProductSelection:
        exchanges.advancedExchanges ?? AdvancedExchanges.ENABLED,
      instant: exchanges.allowInstant,
      instantHoldAmount: exchanges.instantHoldAmount,
      instantExchangeOnly: exchanges.instantExchangeOnly ?? false,
      instantExpirationDays: exchanges.instantExpirationDays,
      excludeProductTags: exchanges.excludeProductTags ?? [],
      excludeProductSkus: exchanges.excludeProductSkus ?? [],
      excludeProductMetafields: exchanges.excludeProductMetafields ?? [],
      excludeVariantMetafields: exchanges.excludeVariantMetafields ?? [],
      inventoryMinimum: settings.inventory.minimum_stock,
      hideOutOfStockVariants: exchanges.hideOutOfStockVariants ?? false,
      hideUnpublishedVariants: exchanges.hideUnpublishedVariants ?? true,
    },
  };
}

export function customerOptionsTeamPatch({
  customerOptions,
}: CustomerOptionsSettings): PortalSettingsPatch {
  return {
    set: {
      "exchanges.advancedExchanges": customerOptions.newProductSelection,
      "exchanges.allowInstant": customerOptions.instant,
      "exchanges.instantHoldAmount": customerOptions.instantHoldAmount,
      "exchanges.instantExchangeOnly": customerOptions.instantExchangeOnly,
      "exchanges.instantExpirationDays": customerOptions.instantExpirationDays,
      "exchanges.excludeProductTags": customerOptions.excludeProductTags,
      "exchanges.excludeProductSkus": customerOptions.excludeProductSkus,
      "exchanges.excludeProductMetafields":
        customerOptions.excludeProductMetafields,
      "exchanges.excludeVariantMetafields":
        customerOptions.excludeVariantMetafields,
      "inventory.minimum_stock": customerOptions.inventoryMinimum,
      "exchanges.hideOutOfStockVariants":
        customerOptions.hideOutOfStockVariants,
      "exchanges.hideUnpublishedVariants":
        customerOptions.hideUnpublishedVariants,
    },
    unset: [],
  };
}
